import React, { useEffect, useRef, useState } from 'react';
import KeenSlider from 'keen-slider';
import 'keen-slider/keen-slider.min.css';
import CarouselItem from '../CarouselItem';
import ProgressDots from '../../ProgressDots';
import styles from './index.module.scss';

const AUTOSCROLL_INTERVAL = 5000;
const ANIMATION_DURATION = 1500;

const Carousel = ({
  items = [],
  freeze = false,
  packageType = 'challenge',
  isWeb = false,
  onWebNavigate,
  currentZoneName,
  zoneOrder,
  packageId,
  packageIndex,
}) => {
  const sliderRef = useRef(null);
  const keenSliderInstance = useRef(null);
  const timerRef = useRef(null);
  const prevIndexRef = useRef(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Track color offset to handle mismatched loop lengths (e.g., 6 items but 4 colors).
  // Without this, colors would reset to the first color when the carousel loops,
  // instead of continuing the sequence.
  const [colorOffset, setColorOffset] = useState(0);

  const startAutoScroll = () => {
    if (!keenSliderInstance.current || freeze) return;
    stopAutoScroll();
    timerRef.current = setInterval(() => {
      const slider = keenSliderInstance.current;
      if (!slider) return;
      // Use moveToIdx with rel+1 to always go forward, avoiding loop reset glitch
      const nextIndex = slider.track.details.rel + 1;
      slider.moveToIdx(nextIndex);
    }, AUTOSCROLL_INTERVAL);
  };

  const stopAutoScroll = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (!sliderRef.current || items.length <= 1) {
      return;
    }

    // Initialize Keen-slider
    const slider = new KeenSlider(sliderRef.current, {
      loop: true,
      defaultAnimation: {
        duration: ANIMATION_DURATION,
        // easing here if needed
      },
      slides: {
        perView: 1,
        spacing: 16,
      },
      slideChanged: (s) => {
        const newIndex = s.track.details.rel;
        // Detect forward loop (went from last item back to first)
        if (prevIndexRef.current === items.length - 1 && newIndex === 0) {
          setColorOffset((prev) => prev + items.length);
        }
        prevIndexRef.current = newIndex;
        setCurrentIndex(newIndex);
      },
      dragStarted: () => {
        stopAutoScroll();
      },
      dragEnded: () => {
        if (!freeze) {
          startAutoScroll();
        }
      },
    });

    keenSliderInstance.current = slider;

    if (!freeze) {
      startAutoScroll();
    }

    return () => {
      stopAutoScroll();
      slider.destroy();
      keenSliderInstance.current = null;
    };
  }, [items.length, freeze]);

  if (!items.length) {
    return null;
  }

  return (
    <div className={styles.carouselWrapper} data-carousel>
      <div ref={sliderRef} className={`keen-slider ${styles.carousel}`}>
        {items.map((item, index) => (
          <div key={item?.id || index} className="keen-slider__slide">
            <CarouselItem
              item={item}
              index={index}
              colorOffset={colorOffset}
              packageType={packageType}
              isWeb={isWeb}
              onWebNavigate={onWebNavigate}
              currentZoneName={currentZoneName}
              zoneOrder={zoneOrder}
              packageId={packageId}
              packageIndex={packageIndex}
            />
          </div>
        ))}
      </div>
      <ProgressDots
        count={items.length}
        activeIndex={currentIndex}
        className={styles.dots}
      />
    </div>
  );
};

export default Carousel;
